import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  type OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import {
  CariButtonComponent,
  CariInputComponent,
  CariInputContainerComponent,
  CariInputErrorComponent,
  UIComponentsModule,
} from "@cariloop/ui-components";
import { TranslateModule } from "@ngx-translate/core";
import { AccountService } from "app/shared/services/account.service";
import { DevicePlatformService } from "app/shared/services/device-platform.service";
import { MfaService } from "app/shared/services/mfa.service";
import { LoadingComponent } from "app/widgets/loading-component/loading.component";

const GOOGLE_DESKTOP_LINK =
  "https://support.google.com/accounts/answer/1066447?hl=en&co=GENIE.Platform%3DAndroid";
const GOOGLE_ANDROID_LINK =
  "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2";
const GOOGLE_IOS_LINK =
  "https://apps.apple.com/us/app/google-authenticator/id388497605";

const MICROSOFT_DESKTOP_LINK =
  "https://support.microsoft.com/en-us/account-billing/download-microsoft-authenticator-351498fc-850a-45da-b7b6-27e523b8702a";
const MICROSOFT_IOS_LINK =
  "https://apps.apple.com/us/app/microsoft-authenticator/id983156458";
const MICROSOFT_ANDROID_LINK =
  "https://play.google.com/store/apps/details?id=com.azure.authenticator";

const AUTHY_DESKTOP_LINK = "https://www.authy.com/download/";
const AUTHY_ANDROID_LINK =
  "https://play.google.com/store/apps/details?id=com.authy.authy";
const AUTHY_IOS_LINK = "https://apps.apple.com/us/app/twilio-authy/id494168017";

@Component({
  selector: "app-two-step-verification",
  imports: [
    MatIconModule,
    TranslateModule,
    UIComponentsModule,
    CariButtonComponent,
    LoadingComponent,
    ReactiveFormsModule,
    CariInputComponent,
    CariInputContainerComponent,
    CariInputErrorComponent,
  ],
  templateUrl: "./totp-setup.component.html",
  styleUrls: ["./totp-setup.component.scss"],
})
export class TotpSetupComponent implements OnInit, AfterViewInit {
  @Output() backupCodes = new EventEmitter<string[]>();
  #devicePlatformService = inject(DevicePlatformService);
  #mfaService = inject(MfaService);
  #accountService = inject(AccountService);
  deviceType: string;
  googleAuthenticatorLink: string = "";
  authyLink: string = "";
  microsoftAuthenticatorLink: string = "";
  qrCodeString: string = "";
  qrSecret: string = "";
  mfaToken: string = "";
  isLoadingQrCode: boolean = true;
  @ViewChild("titleEl") titleElRef!: ElementRef;
  protected readonly verificationCode = new FormControl("", {
    validators: Validators.required,
  });

  ngOnInit() {
    this.deviceType = this.#devicePlatformService.getDeviceType();
    this.googleAuthenticatorLink =
      this.deviceType === "browser"
        ? GOOGLE_DESKTOP_LINK
        : this.deviceType === "android"
          ? GOOGLE_ANDROID_LINK
          : GOOGLE_IOS_LINK;
    this.microsoftAuthenticatorLink =
      this.deviceType === "browser"
        ? MICROSOFT_DESKTOP_LINK
        : this.deviceType === "android"
          ? MICROSOFT_ANDROID_LINK
          : MICROSOFT_IOS_LINK;
    this.authyLink =
      this.deviceType === "browser"
        ? AUTHY_DESKTOP_LINK
        : this.deviceType === "android"
          ? AUTHY_ANDROID_LINK
          : AUTHY_IOS_LINK;
    const account = this.#accountService.getLogged();
    if (account instanceof Error) {
      return;
    }
    this.#mfaService.getTOTPQRCode(account.id).subscribe((data) => {
      this.qrCodeString = data.qrCode;
      this.qrSecret = data.secretKey;
      this.mfaToken = data.mfaToken;
      this.isLoadingQrCode = false;
    });
  }

  copySecret() {
    navigator.clipboard.writeText(this.qrSecret);
  }

  ngAfterViewInit(): void {
    this.titleElRef.nativeElement.focus();
  }

  turnOnVerification() {
    if (this.verificationCode.value === "") {
      this.verificationCode.setErrors({ required: true });
      this.verificationCode.markAsTouched();
      return;
    }
    const account = this.#accountService.getLogged();
    if (account instanceof Error) {
      return;
    }
    this.#mfaService
      .enableTOTPVerification(
        this.mfaToken,
        this.verificationCode.value,
        account.id,
      )
      .subscribe({
        next: (backupCodes) => {
          this.backupCodes.emit(backupCodes);
        },
        error: (error) => {
          this.verificationCode.setErrors({ invalid: true });
        },
      });
  }
}
